import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as PendingIcon,
  Warning as OverdueIcon,
} from '@mui/icons-material';
import { newHiresApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  NewHireWithChecklist,
  NewHireUpdate,
  NewHireStatus,
  NewHireChecklistEntry,
} from '../types';

const statusLabels: Record<NewHireStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  terminated: 'Terminated',
};

const NewHireDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [editData, setEditData] = useState<NewHireUpdate>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['all']));

  // Query
  const { data: newHire, isLoading } = useQuery(
    ['new-hire', id],
    () => newHiresApi.getNewHire(Number(id)),
    { enabled: !!id }
  );

  // Mutations
  const updateMutation = useMutation(
    (data: NewHireUpdate) => newHiresApi.updateNewHire(Number(id), data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['new-hire', id]);
        queryClient.invalidateQueries(['new-hires']);
        setEditDialogOpen(false);
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to update new hire');
      },
    }
  );

  const updateChecklistMutation = useMutation(
    ({ entryId, data }: { entryId: number; data: { is_completed: boolean } }) =>
      newHiresApi.updateChecklistEntry(Number(id), entryId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['new-hire', id]);
        queryClient.invalidateQueries(['new-hires']);
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to update checklist item');
      },
    }
  );

  const openEditDialog = () => {
    if (newHire) {
      setEditData({
        employee_name: newHire.employee_name,
        position: newHire.position,
        department: newHire.department,
        hire_date: newHire.hire_date,
        status: newHire.status,
        notes: newHire.notes,
      });
      setEditDialogOpen(true);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleChecklistToggle = (entry: NewHireChecklistEntry) => {
    updateChecklistMutation.mutate({
      entryId: entry.id,
      data: { is_completed: !entry.is_completed },
    });
  };

  // Group checklist items by category
  const groupedItems = React.useMemo(() => {
    if (!newHire?.checklist_items) return {};

    const groups: Record<string, NewHireChecklistEntry[]> = {};
    newHire.checklist_items.forEach((item) => {
      const category = item.checklist_item?.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    return groups;
  }, [newHire?.checklist_items]);

  const isOverdue = (entry: NewHireChecklistEntry) => {
    if (entry.is_completed || !entry.due_date) return false;
    const [year, month, day] = entry.due_date.split('-').map(Number);
    const dueDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!newHire) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">New hire not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/new-hires')}
          sx={{ mb: 2 }}
        >
          Back to New Hires
        </Button>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {newHire.employee_name}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {newHire.position}
                {newHire.department && ` - ${newHire.department}`}
              </Typography>
              <Chip
                label={statusLabels[newHire.status]}
                color={newHire.status === 'completed' ? 'success' : newHire.status === 'terminated' ? 'error' : 'primary'}
                sx={{ mr: 1 }}
              />
              <Chip
                label={`Hire Date: ${(() => {
                  const [year, month, day] = newHire.hire_date.split('-').map(Number);
                  return new Date(year, month - 1, day).toLocaleDateString('en-US');
                })()}`}
                variant="outlined"
              />
            </Box>
            {isManager && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={openEditDialog}
              >
                Edit
              </Button>
            )}
          </Box>

          {/* Progress */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Onboarding Progress</Typography>
              <Typography variant="body1" fontWeight="bold">
                {newHire.progress_percentage.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={newHire.progress_percentage}
              sx={{ height: 10, borderRadius: 5 }}
              color={newHire.progress_percentage === 100 ? 'success' : 'primary'}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {newHire.completed_items} of {newHire.total_items} items completed
            </Typography>
          </Box>

          {newHire.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography variant="body1">{newHire.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* Checklist */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Onboarding Checklist
          </Typography>

          {Object.entries(groupedItems).length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No checklist items configured. Go to Configure Checklist to add items.
            </Typography>
          ) : (
            Object.entries(groupedItems).map(([category, items]) => {
              const completedInCategory = items.filter((i) => i.is_completed).length;
              const isExpanded = expandedCategories.has(category) || expandedCategories.has('all');

              return (
                <Box key={category} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.200' },
                    }}
                    onClick={() => toggleCategory(category)}
                  >
                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                    <Typography variant="subtitle1" sx={{ ml: 1, flexGrow: 1 }}>
                      {category}
                    </Typography>
                    <Chip
                      label={`${completedInCategory}/${items.length}`}
                      size="small"
                      color={completedInCategory === items.length ? 'success' : 'default'}
                    />
                  </Box>

                  <Collapse in={isExpanded}>
                    <List dense>
                      {items.map((entry, index) => (
                        <React.Fragment key={entry.id}>
                          {index > 0 && <Divider />}
                          <ListItem
                            sx={{
                              py: 1.5,
                              bgcolor: entry.is_completed ? 'success.50' : isOverdue(entry) ? 'error.50' : 'transparent',
                            }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={entry.is_completed}
                                onChange={() => handleChecklistToggle(entry)}
                                disabled={!isManager || updateChecklistMutation.isLoading}
                                icon={
                                  isOverdue(entry) ? (
                                    <OverdueIcon color="error" />
                                  ) : (
                                    <PendingIcon />
                                  )
                                }
                                checkedIcon={<CompletedIcon color="success" />}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    sx={{
                                      textDecoration: entry.is_completed ? 'line-through' : 'none',
                                    }}
                                  >
                                    {entry.checklist_item?.name}
                                  </Typography>
                                  {entry.checklist_item?.is_required && (
                                    <Chip label="Required" size="small" color="warning" />
                                  )}
                                  {isOverdue(entry) && (
                                    <Chip label="Overdue" size="small" color="error" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {entry.checklist_item?.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {entry.checklist_item.description}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                    {entry.due_date && (
                                      <Typography variant="caption" color={isOverdue(entry) ? 'error' : 'text.secondary'}>
                                        Due: {(() => {
                                          const [year, month, day] = entry.due_date.split('-').map(Number);
                                          return new Date(year, month - 1, day).toLocaleDateString('en-US');
                                        })()}
                                      </Typography>
                                    )}
                                    {entry.is_completed && entry.completed_by && (
                                      <Typography variant="caption" color="text.secondary">
                                        Completed by {entry.completed_by.first_name} {entry.completed_by.last_name}
                                        {entry.completed_at && ` on ${new Date(entry.completed_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })
          )}
        </Paper>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit New Hire</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee Name"
                value={editData.employee_name || ''}
                onChange={(e) => setEditData({ ...editData, employee_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Position"
                value={editData.position || ''}
                onChange={(e) => setEditData({ ...editData, position: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Department"
                value={editData.department || ''}
                onChange={(e) => setEditData({ ...editData, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Hire Date"
                type="date"
                value={editData.hire_date || ''}
                onChange={(e) => setEditData({ ...editData, hire_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editData.status || ''}
                  label="Status"
                  onChange={(e) => setEditData({ ...editData, status: e.target.value as NewHireStatus })}
                >
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate(editData)}
            disabled={updateMutation.isLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NewHireDetail;
